import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  User,
  Phone,
  Mail,
  Calendar,
  Crown,
  Star,
  TrendingUp,
  TrendingDown,
  Gift,
  Edit,
  Check,
  Plus,
  Minus,
  History,
  Award,
  AlertCircle,
} from 'lucide-react';
import {
  useMember,
  useTransactions,
  type LoyaltyMember,
} from '../../../api/queries/loyalty.queries';
import {
  useUpdateMember,
  useAdjustPoints,
  useClaimBirthdayReward,
} from '../../../api/mutations/loyalty.mutations';
import { useAuthStore } from '../../../stores/auth.store';

interface MemberDetailModalProps {
  member: LoyaltyMember;
  onClose: () => void;
  onRefresh: () => void;
}

type TabType = 'details' | 'transactions' | 'edit';

export default function MemberDetailModal({ member, onClose, onRefresh }: MemberDetailModalProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();

  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState<number>(0);
  const [adjustReason, setAdjustReason] = useState('');
  const [txPage, setTxPage] = useState(1);

  // Edit form state
  const [editForm, setEditForm] = useState({
    name: member.name,
    email: member.email || '',
    nickname: member.nickname || '',
    birthdate: member.birthdate ? member.birthdate.split('T')[0] : '',
    gender: member.gender || '',
    notes: member.notes || '',
    smsOptIn: member.smsOptIn,
    emailOptIn: member.emailOptIn,
  });

  // Queries
  const { data: memberData } = useMember(member._id);
  const { data: txData } = useTransactions(member._id, { page: txPage, limit: 10 });

  // Mutations
  const updateMutation = useUpdateMember(member._id);
  const adjustMutation = useAdjustPoints();
  const birthdayMutation = useClaimBirthdayReward();

  const canManage = user?.role === 'HQ_ADMIN';

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'diamond':
        return 'text-sky-600 bg-sky-50 border-sky-200';
      case 'platinum':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'gold':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'silver':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-orange-600 bg-orange-50 border-orange-200';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString(i18n.language === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'earn':
      case 'bonus':
      case 'welcome':
      case 'tier_bonus':
      case 'referral_earn':
      case 'referral_bonus':
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'redeem':
      case 'expire':
        return <TrendingDown className="h-4 w-4 text-red-500" />;
      case 'birthday':
        return <Gift className="h-4 w-4 text-pink-500" />;
      case 'adjustment':
        return <Edit className="h-4 w-4 text-blue-500" />;
      default:
        return <Star className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleUpdateMember = async () => {
    try {
      await updateMutation.mutateAsync({
        name: editForm.name,
        email: editForm.email || undefined,
        nickname: editForm.nickname || undefined,
        birthdate: editForm.birthdate || undefined,
        gender: editForm.gender as any || undefined,
        notes: editForm.notes || undefined,
        smsOptIn: editForm.smsOptIn,
        emailOptIn: editForm.emailOptIn,
      });
      setActiveTab('details');
      onRefresh();
    } catch (error) {
      console.error('Failed to update member:', error);
    }
  };

  const handleAdjustPoints = async () => {
    if (!adjustAmount || !adjustReason) return;
    try {
      await adjustMutation.mutateAsync({
        memberId: member._id,
        points: adjustAmount,
        reason: adjustReason,
      });
      setShowAdjustModal(false);
      setAdjustAmount(0);
      setAdjustReason('');
      onRefresh();
    } catch (error) {
      console.error('Failed to adjust points:', error);
    }
  };

  const handleClaimBirthday = async () => {
    if (!user?.branchId) return;
    try {
      await birthdayMutation.mutateAsync({
        memberId: member._id,
        branchId: user.branchId,
      });
      onRefresh();
    } catch (error) {
      console.error('Failed to claim birthday:', error);
    }
  };

  const tabs = [
    { id: 'details', label: t('loyalty.details'), icon: User },
    { id: 'transactions', label: t('loyalty.transactions'), icon: History },
    { id: 'edit', label: t('common.edit'), icon: Edit, hidden: !canManage },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-lg bg-card flex flex-col">
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${getTierColor(member.tier)}`}>
              <Crown className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">{member.name}</h2>
              <p className="text-sm text-muted-foreground">
                {member.memberId} • {member.phone}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${getTierColor(member.tier)}`}>
              {member.tier}
            </span>
            {member.isBanned && (
              <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-700">
                {t('loyalty.banned')}
              </span>
            )}
            <button onClick={onClose} className="p-2 hover:bg-secondary rounded">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b px-4">
          <div className="flex gap-1">
            {tabs.filter(t => !t.hidden).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent hover:text-primary'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Points Card */}
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('loyalty.currentPoints')}</p>
                    <p className="text-3xl font-bold text-primary">{member.currentPoints.toLocaleString()}</p>
                  </div>
                  {canManage && (
                    <button
                      onClick={() => setShowAdjustModal(true)}
                      className="px-3 py-1.5 rounded bg-primary text-primary-foreground text-sm hover:bg-primary/90 flex items-center gap-1"
                    >
                      <Edit className="h-4 w-4" />
                      {t('loyalty.adjustPoints')}
                    </button>
                  )}
                </div>
                {memberData?.pointsToNextTier && memberData.nextTierConfig && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span>{memberData.pointsToNextTier.toLocaleString()} pts to {memberData.nextTierConfig.tier}</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{
                          width: `${Math.min(100, (member.lifetimePoints / memberData.nextTierConfig.minLifetimePoints) * 100)}%`
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg border text-center">
                  <p className="text-2xl font-bold">{member.lifetimePoints.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{t('loyalty.lifetimePoints')}</p>
                </div>
                <div className="p-4 rounded-lg border text-center">
                  <p className="text-2xl font-bold">฿{member.lifetimeSpend.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">{t('loyalty.lifetimeSpend')}</p>
                </div>
                <div className="p-4 rounded-lg border text-center">
                  <p className="text-2xl font-bold">{member.totalOrders}</p>
                  <p className="text-xs text-muted-foreground">{t('loyalty.totalOrders')}</p>
                </div>
              </div>

              {/* Birthday Reward */}
              {memberData?.canClaimBirthday && (
                <div className="p-4 rounded-lg bg-pink-50 border border-pink-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Gift className="h-8 w-8 text-pink-500" />
                    <div>
                      <p className="font-medium text-pink-700">{t('loyalty.birthdayRewardAvailable')}</p>
                      <p className="text-sm text-pink-600">
                        {memberData.tierConfig?.birthdayBonus.toLocaleString()} {t('loyalty.pointsBonus')}
                        {memberData.tierConfig?.birthdayFreeItem && ` + ${t('loyalty.freeItem')}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClaimBirthday}
                    disabled={birthdayMutation.isPending}
                    className="px-4 py-2 rounded bg-pink-500 text-white text-sm hover:bg-pink-600 disabled:opacity-50"
                  >
                    {t('loyalty.claim')}
                  </button>
                </div>
              )}

              {/* Member Info */}
              <div className="space-y-4">
                <h3 className="font-medium">{t('loyalty.memberInfo')}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{member.phone}</span>
                    {member.phoneVerified && <Check className="h-4 w-4 text-green-500" />}
                  </div>
                  {member.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span>{member.email}</span>
                      {member.emailVerified && <Check className="h-4 w-4 text-green-500" />}
                    </div>
                  )}
                  {member.birthdate && (
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span>{formatDate(member.birthdate)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    <span>{t('loyalty.memberSince')} {formatDate(member.registeredAt)}</span>
                  </div>
                </div>
              </div>

              {/* Referral */}
              <div className="p-4 rounded-lg bg-secondary/50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('loyalty.referralCode')}</p>
                    <p className="font-mono font-bold">{member.referralCode}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">{t('loyalty.referrals')}</p>
                    <p className="font-bold">{member.referralCount}</p>
                  </div>
                </div>
              </div>

              {/* Tags */}
              {member.tags && member.tags.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">{t('loyalty.tags')}</p>
                  <div className="flex flex-wrap gap-2">
                    {member.tags.map((tag) => (
                      <span key={tag} className="px-2 py-1 rounded bg-secondary text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {member.notes && (
                <div>
                  <p className="text-sm font-medium mb-2">{t('loyalty.notes')}</p>
                  <p className="text-sm text-muted-foreground bg-secondary/50 p-3 rounded">{member.notes}</p>
                </div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div className="space-y-4">
              {txData?.transactions && txData.transactions.length > 0 ? (
                <>
                  <div className="space-y-2">
                    {txData.transactions.map((tx) => (
                      <div key={tx._id} className="p-3 rounded-lg border flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {getTransactionIcon(tx.type)}
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-muted-foreground">{formatDateTime(tx.createdAt)}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${tx.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {tx.points >= 0 ? '+' : ''}{tx.points.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t('loyalty.balance')}: {tx.balanceAfter.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {txData.pagination.pages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-sm text-muted-foreground">
                        {t('common.page')} {txPage} / {txData.pagination.pages}
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setTxPage(txPage - 1)}
                          disabled={txPage === 1}
                          className="px-3 py-1 rounded border hover:bg-secondary disabled:opacity-50"
                        >
                          {t('common.previous')}
                        </button>
                        <button
                          onClick={() => setTxPage(txPage + 1)}
                          disabled={txPage >= txData.pagination.pages}
                          className="px-3 py-1 rounded border hover:bg-secondary disabled:opacity-50"
                        >
                          {t('common.next')}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>{t('loyalty.noTransactions')}</p>
                </div>
              )}
            </div>
          )}

          {/* Edit Tab */}
          {activeTab === 'edit' && canManage && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t('loyalty.name')} *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.nickname')}</label>
                  <input
                    type="text"
                    value={editForm.nickname}
                    onChange={(e) => setEditForm({ ...editForm, nickname: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.email')}</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.birthdate')}</label>
                  <input
                    type="date"
                    value={editForm.birthdate}
                    onChange={(e) => setEditForm({ ...editForm, birthdate: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t('loyalty.gender')}</label>
                  <select
                    value={editForm.gender}
                    onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                    className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="male">{t('loyalty.male')}</option>
                    <option value="female">{t('loyalty.female')}</option>
                    <option value="other">{t('loyalty.other')}</option>
                    <option value="prefer_not_to_say">{t('loyalty.preferNotToSay')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">{t('loyalty.notes')}</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">{t('loyalty.preferences')}</p>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.smsOptIn}
                    onChange={(e) => setEditForm({ ...editForm, smsOptIn: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">{t('loyalty.smsOptIn')}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editForm.emailOptIn}
                    onChange={(e) => setEditForm({ ...editForm, emailOptIn: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">{t('loyalty.emailOptIn')}</span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  onClick={() => setActiveTab('details')}
                  className="px-4 py-2 rounded-lg border hover:bg-secondary"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleUpdateMember}
                  disabled={updateMutation.isPending || !editForm.name}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border hover:bg-secondary"
          >
            {t('common.close')}
          </button>
        </div>
      </div>

      {/* Adjust Points Modal */}
      {showAdjustModal && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => e.target === e.currentTarget && setShowAdjustModal(false)}
        >
          <div className="w-full max-w-md rounded-lg bg-card p-6">
            <h3 className="text-lg font-bold mb-4">{t('loyalty.adjustPoints')}</h3>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t('loyalty.amount')}</label>
                <div className="flex items-center gap-2 mt-1">
                  <button
                    onClick={() => setAdjustAmount(Math.min(0, adjustAmount))}
                    className={`p-2 rounded ${adjustAmount < 0 ? 'bg-red-100 text-red-600' : 'bg-secondary'}`}
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    value={Math.abs(adjustAmount)}
                    onChange={(e) => setAdjustAmount(
                      adjustAmount >= 0 ? Number(e.target.value) : -Number(e.target.value)
                    )}
                    className="flex-1 rounded-lg border bg-background px-3 py-2 text-center"
                  />
                  <button
                    onClick={() => setAdjustAmount(Math.abs(adjustAmount))}
                    className={`p-2 rounded ${adjustAmount >= 0 ? 'bg-green-100 text-green-600' : 'bg-secondary'}`}
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {adjustAmount >= 0 ? t('loyalty.addPoints') : t('loyalty.deductPoints')}
                </p>
              </div>

              <div>
                <label className="text-sm font-medium">{t('loyalty.reason')} *</label>
                <input
                  type="text"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  placeholder={t('loyalty.adjustReasonPlaceholder')}
                  className="w-full mt-1 rounded-lg border bg-background px-3 py-2"
                />
              </div>

              <div className="p-3 rounded bg-secondary/50 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">
                  {t('loyalty.adjustWarning')}
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowAdjustModal(false)}
                className="px-4 py-2 rounded-lg border hover:bg-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleAdjustPoints}
                disabled={adjustMutation.isPending || !adjustAmount || !adjustReason}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {t('loyalty.confirmAdjust')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
